import { Box, Typography, Drawer, Divider, Stack } from '@vapor/material'
import { styled } from '@mui/material/styles'
import React, { useMemo } from 'react'
import useBreakpoints from 'src/app/hooks/breakpoints/use-breakpoints'
import { PAGE_WIDTH } from 'src/app/components/page/page'
import { HICLoading } from 'hic-components'

export type FormLayoutDrawerProps = React.PropsWithChildren<{
  title: string
  subtitle?: React.ReactNode
  open: boolean
  onClose: () => void
  width?: string | number
  toolbar?: React.ReactNode
  hideTitle?: boolean
  leftNodes?: React.ReactNode[]
  rightNodes?: React.ReactNode[]
  removePadding?: true
  tight?: boolean
  large?: boolean
  titleOnLeft?: boolean
  loading?: boolean
  id?: string
  superTight?: boolean
}>

const HiCDrawer = styled(Drawer)(({ theme }) => ({
  zIndex: theme.zIndex.modal,
})) as typeof Drawer

export default function FormLayoutDrawer(props: FormLayoutDrawerProps) {
  const width = props.width
  const { sm, md } = useBreakpoints()

  const titleSize = useMemo(() => {
    if (props.subtitle) {
      if (md) return 'titleSmall'
    } else {
      if (md) return 'titleMedium'
      else if (sm) return 'titleSmall'
    }
    return 'titleXSmall'
  }, [sm, md, props.subtitle])

  return (
    <HiCDrawer //
      anchor="right"
      open={props.open}
      onClose={props.onClose}
      PaperProps={{
        sx: {
          width: props.large
            ? { xs: '100% !important', xl: `${PAGE_WIDTH} !important` }
            : props.tight
            ? { xs: '100% !important', lg: '800px !important' }
            : props.superTight
            ? { xs: '100% !important', sm: '500px !important' }
            : typeof width === 'string'
            ? `${width} !important`
            : `${width}px !important`,
          minWidth: props.superTight ? '0px !important' : '40%',
          backgroundColor: 'white',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        },
      }}
    >
      {props.hideTitle ? undefined : (
        <>
          <div style={{ padding: '16px 16px 0px 16px' }} id={props.hideTitle ? undefined : props.id}>
            {props.loading && <HICLoading />}
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }} mb={props.rightNodes ? 1 : undefined}>
              {props.leftNodes?.map((node, index) => {
                return <React.Fragment key={index}>{node}</React.Fragment>
              })}
              <Stack flexGrow={props.leftNodes ? 0 : 2}>
                <Stack direction="row" justifyContent={props.titleOnLeft ? 'start' : 'center'} alignItems="center">
                  <Typography variant={titleSize}>{props.title}</Typography>
                </Stack>
                <>{props.subtitle}</>
              </Stack>

              {props.rightNodes?.map((node, index) => {
                return <React.Fragment key={index}>{node}</React.Fragment>
              })}
            </Box>
          </div>
          <Divider />
        </>
      )}
      <Box component="div" p={props.removePadding ? undefined : { xs: 1, sm: 2 }} sx={{ flexGrow: 1 }} id={props.hideTitle ? props.id : undefined}>
        {props.children}
      </Box>
      {props.toolbar && (
        <div
          style={{
            backgroundColor: 'hsl(199deg 4% 95%)',
            position: 'sticky',
            bottom: '0px',
            zIndex: 'inherit',
          }}
        >
          <div style={{ minHeight: '40px', padding: '16px' }}>{props.toolbar}</div>
        </div>
      )}
    </HiCDrawer>
  )
}
